import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PhotoUpload from "@/components/PhotoUpload";
import CategoryPicker from "@/components/CategoryPicker";
import RichTextEditor, { stripHtml } from "@/components/RichTextEditor";
import Modal from "@/components/Modal";
import DOMPurify from "dompurify";
import { ArrowLeft, Send, Save, Eye, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const draftKey = (userId, editId) => `bizma:blog:draft:${userId || "anon"}:${editId || "new"}`;

export default function BlogForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);
  const [form, setForm] = useState({ title: "", content: "", category: "", photo_path: "" });
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [restoredDraft, setRestoredDraft] = useState(null); // { form, savedAt } if restored
  const loadedRef = useRef(false);
  const key = draftKey(user?.user_id, id);

  // Initial load: check draft in localStorage OR fetch from server (edit)
  useEffect(() => {
    (async () => {
      // Check localStorage draft first
      try {
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.savedAt && Date.now() - parsed.savedAt < DRAFT_TTL_MS) {
            setRestoredDraft(parsed);
          } else {
            localStorage.removeItem(key);
          }
        }
      } catch (e) { /* ignore corrupt */ }

      if (isEdit) {
        try {
          const { data } = await api.get(`/blogs/${id}`);
          setForm({ title: data.title, content: data.content, category: data.category, photo_path: data.photo_path || "" });
        } catch (err) { toast.error("Gagal muat"); }
      }
      loadedRef.current = true;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  // Auto-save draft every 10s
  useEffect(() => {
    const iv = setInterval(() => {
      if (!loadedRef.current) return;
      // Skip empty drafts
      const plain = stripHtml(form.content).trim();
      if (!form.title.trim() && !plain && !form.photo_path && !form.category) return;
      try {
        localStorage.setItem(key, JSON.stringify({ form, savedAt: Date.now() }));
        setLastSavedAt(Date.now());
      } catch (e) { /* quota exceeded — ignore */ }
    }, 10000);
    return () => clearInterval(iv);
  }, [form, key]);

  const restoreDraft = () => {
    if (restoredDraft?.form) {
      setForm(restoredDraft.form);
      toast.success("Draf dipulihkan");
    }
    setRestoredDraft(null);
  };
  const dismissDraft = () => {
    localStorage.removeItem(key);
    setRestoredDraft(null);
  };

  const submit = async (e) => {
    e?.preventDefault();
    const plain = stripHtml(form.content).trim();
    if (!form.title.trim() || !plain || !form.category) { toast.error("Judul, kategori, dan isi wajib"); return; }
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/blogs/${id}`, form);
      else await api.post("/blogs", form);
      localStorage.removeItem(key);
      toast.success(isEdit ? "Artikel diperbarui" : "Artikel dipublikasi");
      nav("/app/blog");
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const savedLabel = lastSavedAt ? `Draf tersimpan ${new Date(lastSavedAt).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}` : "Draf belum tersimpan";

  return (
    <Layout>
      <Link to="/app/blog" className="inline-flex items-center gap-2 text-sm text-bizma-subtext mb-6" data-testid="back-to-blog"><ArrowLeft size={16}/> Kembali</Link>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold">Berbagi Ilmu</div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold mt-1">{isEdit ? "Edit Artikel" : "Tulis Artikel"}</h1>
        </div>
        <div className="text-xs text-bizma-subtext" data-testid="draft-status">{savedLabel}</div>
      </div>

      {restoredDraft && (
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl bg-bizma-sage/10 border border-bizma-sage/30" data-testid="restore-draft-banner">
          <div className="text-sm text-bizma-ink">
            Ada draf tersimpan dari {new Date(restoredDraft.savedAt).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}. Pulihkan?
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={restoreDraft} className="btn-primary text-sm py-2 px-4" data-testid="restore-draft-btn"><RotateCcw size={14}/> Pulihkan</button>
            <button type="button" onClick={dismissDraft} className="btn-outline text-sm py-2 px-4" data-testid="dismiss-draft-btn">Buang</button>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="blog-form">
        <div className="lg:col-span-2 space-y-4">
          <div className="felt-card p-5 sm:p-6">
            <label className="text-sm font-medium">Judul <span className="text-bizma-terracotta">*</span></label>
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Judul artikel yang menarik..."
              className="mt-3 w-full bg-transparent text-2xl sm:text-3xl font-display font-bold outline-none placeholder:text-bizma-subtext/60 border-0 border-b border-transparent focus:border-bizma-border transition-colors py-2"
              data-testid="blog-title"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Isi Artikel <span className="text-bizma-terracotta">*</span> <span className="text-xs text-bizma-subtext font-normal">— tarik/tempel gambar untuk sisipkan langsung</span></label>
            <RichTextEditor value={form.content} onChange={(html) => setForm({ ...form, content: html })} />
          </div>
        </div>

        <aside className="space-y-4">
          <div className="felt-card p-5">
            <label className="text-sm font-medium">Foto Sampul <span className="text-bizma-subtext text-xs font-normal">(opsional)</span></label>
            <div className="mt-3">
              <PhotoUpload value={form.photo_path} onChange={(p) => setForm({ ...form, photo_path: p })} shape="square" testId="blog-photo" />
            </div>
          </div>
          <div className="felt-card p-5">
            <label className="text-sm font-medium">Kategori <span className="text-bizma-terracotta">*</span></label>
            <div className="mt-3">
              <CategoryPicker value={form.category} onChange={(v) => setForm({ ...form, category: v })} endpoint="/blog-categories" testId="blog-category" />
            </div>
          </div>
          <div className="felt-card p-5 space-y-3">
            <button type="button" onClick={() => setPreviewOpen(true)} className="btn-outline w-full h-11" data-testid="blog-preview-btn"><Eye size={16}/> Pratinjau</button>
            <button disabled={busy} className="btn-primary w-full h-12" data-testid="blog-submit">
              {isEdit ? <><Save size={16}/> {busy ? "Menyimpan..." : "Simpan Perubahan"}</> : <><Send size={16}/> {busy ? "Menerbitkan..." : "Publikasikan"}</>}
            </button>
            <Link to="/app/blog" className="btn-outline w-full h-11" data-testid="blog-cancel">Batal</Link>
          </div>
        </aside>
      </form>

      {previewOpen && (
        <Modal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          title="Pratinjau Artikel"
          size="xl"
          testId="blog-preview-modal"
          footer={
            <>
              <button type="button" onClick={() => setPreviewOpen(false)} className="btn-outline flex-1" data-testid="preview-close-btn">Kembali Edit</button>
              <button type="button" onClick={() => { setPreviewOpen(false); submit(); }} disabled={busy} className="btn-primary flex-1" data-testid="preview-publish-btn">
                <Send size={16}/> {isEdit ? "Simpan" : "Publikasikan"}
              </button>
            </>
          }
        >
          <div className="pb-2" data-testid="preview-content">
            {form.photo_path && (
              <div className="aspect-video bg-bizma-muted rounded-2xl overflow-hidden mb-5">
                <img src={fileUrl(form.photo_path)} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold">{form.category || "Kategori"}</div>
            <h1 className="text-3xl sm:text-4xl font-display font-bold mt-2" data-testid="preview-title">{form.title || "Judul artikel"}</h1>
            <div className="mt-6 prose prose-neutral max-w-none text-[#2D2A26] leading-relaxed"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.content || "<p><em>Belum ada isi artikel.</em></p>") }} />
          </div>
        </Modal>
      )}
    </Layout>
  );
}
